import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Button, BackButton } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { FormField, Input, Textarea, Checkbox } from '../components/common/Form';
import ClubBanner from '../components/clubs/ClubBanner';
import ClubLogo from '../components/clubs/ClubLogo';
import ImageCropModal from '../components/image/ImageCropModal';
import { uploadClubBanner, removeClubBanner, getClubBannerPublicUrl } from '../services/clubBannerService';
import { uploadClubLogo, removeClubLogo, getClubLogoPublicUrl } from '../services/clubLogoService';
import { DB_DEFAULTS, getDefaultStartingElo } from '../utils/constants';

const BANNER_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const BANNER_MAX_BYTES = 2 * 1024 * 1024; // 2MB
const BANNER_ASPECT = 3; // 3:1 banner

const LOGO_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5MB
const LOGO_ASPECT = 1; // 1:1 square/circle

const ClubSettings = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Banner upload state
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [removingBanner, setRemovingBanner] = useState(false);
  const [bannerError, setBannerError] = useState(null);
  const [bannerSuccess, setBannerSuccess] = useState(null);
  const [bannerCropModalOpen, setBannerCropModalOpen] = useState(false);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [bannerCropImageUrl, setBannerCropImageUrl] = useState(null); // For re-cropping existing banner

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [logoSuccess, setLogoSuccess] = useState(null);
  const [logoCropModalOpen, setLogoCropModalOpen] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoCropImageUrl, setLogoCropImageUrl] = useState(null); // For re-cropping existing logo

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    location_details: '',
    description: '',
    is_public: true,
    contact_email: '',
    website_url: '',
    facebook: '',
    twitter: '',
    instagram: '',
    strength_rating: DB_DEFAULTS.CLUB_STRENGTH_RATING,
    session_auto_end_hours: 1,
    session_startup_timeout_hours: 2
  });

  const [formErrors, setFormErrors] = useState({});

  // Get navigation context from state
  const fromMyClubs = location.state?.fromMyClubs;
  const fromHomepage = location.state?.fromHomepage;
  const fromManageClubs = location.state?.fromManageClubs;

  useEffect(() => {
    if (clubId && currentUser?.id) {
      fetchClubDetails();
    }
  }, [clubId, currentUser?.id]);

  const fetchClubDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get club details
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) throw clubError;

      // Get user's role in this club
      const { data: roleData, error: roleError } = await supabase
        .from('players_club')
        .select('club_role')
        .eq('player_id', currentUser.id)
        .eq('club_id', clubId)
        .single();

      if (roleError) throw roleError;

      // Check if user has management permissions
      if (!['admin', 'owner'].includes(roleData.club_role)) {
        throw new Error('You do not have permission to view club settings');
      }

      setClub(clubData);
      setUserRole(roleData.club_role);

      // Parse social media JSONB
      const socialMedia = clubData.social_media || {};

      // Populate form with club data
      setFormData({
        name: clubData.name || '',
        location: clubData.location || '',
        location_details: clubData.location_details || '',
        description: clubData.description || '',
        is_public: clubData.is_public !== undefined ? clubData.is_public : true,
        contact_email: clubData.contact_email || '',
        website_url: clubData.website_url || '',
        facebook: socialMedia.facebook || '',
        twitter: socialMedia.twitter || '',
        instagram: socialMedia.instagram || '',
        strength_rating: getDefaultStartingElo(clubData.strength_rating),
        session_auto_end_hours: clubData.session_auto_end_hours || 1,
        session_startup_timeout_hours: clubData.session_startup_timeout_hours || 2
      });
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Club name is required';
    }

    if (formData.contact_email && formData.contact_email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email)) {
        errors.contact_email = 'Please enter a valid email address';
      }
    }

    if (formData.website_url && formData.website_url.trim()) {
      try {
        new URL(formData.website_url);
      } catch {
        errors.website_url = 'Please enter a valid URL (e.g., https://example.com)';
      }
    }

    // Validate social media URLs
    ['facebook', 'twitter', 'instagram'].forEach(platform => {
      if (formData[platform] && formData[platform].trim()) {
        try {
          new URL(formData[platform]);
        } catch {
          errors[platform] = 'Please enter a valid URL';
        }
      }
    });

    // Validate strength rating
    if (formData.strength_rating !== null && formData.strength_rating !== undefined) {
      const strengthRating = parseInt(formData.strength_rating);
      if (isNaN(strengthRating) || strengthRating < 0) {
        errors.strength_rating = 'Strength rating must be a positive number';
      }
    }

    // Validate session timeout hours
    if (formData.session_auto_end_hours < 1 || formData.session_auto_end_hours > 24) {
      errors.session_auto_end_hours = 'Must be between 1 and 24 hours';
    }

    if (formData.session_startup_timeout_hours < 1 || formData.session_startup_timeout_hours > 24) {
      errors.session_startup_timeout_hours = 'Must be between 1 and 24 hours';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setError('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Build social media object
      const socialMedia = {};
      if (formData.facebook?.trim()) socialMedia.facebook = formData.facebook.trim();
      if (formData.twitter?.trim()) socialMedia.twitter = formData.twitter.trim();
      if (formData.instagram?.trim()) socialMedia.instagram = formData.instagram.trim();

      // Prepare update data
      const updateData = {
        name: formData.name.trim(),
        location: formData.location.trim() || null,
        location_details: formData.location_details.trim() || null,
        description: formData.description.trim() || null,
        is_public: formData.is_public,
        contact_email: formData.contact_email.trim() || null,
        website_url: formData.website_url.trim() || null,
        social_media: Object.keys(socialMedia).length > 0 ? socialMedia : null,
        strength_rating: formData.strength_rating !== null && formData.strength_rating !== undefined 
          ? parseInt(formData.strength_rating) 
          : null,
        session_auto_end_hours: formData.session_auto_end_hours,
        session_startup_timeout_hours: formData.session_startup_timeout_hours,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('clubs')
        .update(updateData)
        .eq('id', clubId);

      if (updateError) throw updateError;

      setSuccess('Club settings updated successfully!');
      setIsEditing(false);
      
      // Refresh club data
      await fetchClubDetails();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating club settings:', err);
      setError(err.message || 'Failed to update club settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original club data
    if (club) {
      const socialMedia = club.social_media || {};
      setFormData({
        name: club.name || '',
        location: club.location || '',
        location_details: club.location_details || '',
        description: club.description || '',
        is_public: club.is_public !== undefined ? club.is_public : true,
        contact_email: club.contact_email || '',
        website_url: club.website_url || '',
        facebook: socialMedia.facebook || '',
        twitter: socialMedia.twitter || '',
        instagram: socialMedia.instagram || '',
        strength_rating: getDefaultStartingElo(club.strength_rating),
        session_auto_end_hours: club.session_auto_end_hours || 1,
        session_startup_timeout_hours: club.session_startup_timeout_hours || 2
      });
    }
    setFormErrors({});
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  const handleBannerFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBannerError(null);
    setBannerSuccess(null);

    if (file.size > BANNER_MAX_BYTES) {
      setBannerError(`File is too large. Maximum size is ${BANNER_MAX_BYTES / (1024 * 1024)}MB.`);
      event.target.value = '';
      return;
    }

    const type = (file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type)) {
      setBannerError('Invalid file type. Use JPG, PNG or WebP.');
      event.target.value = '';
      return;
    }

    setSelectedBannerFile(file);
    setBannerCropImageUrl(null); // Clear any existing crop URL
    setBannerCropModalOpen(true);
    event.target.value = '';
  };

  const handleBannerCropClick = async () => {
    if (!club?.banner_image_url) return;

    setBannerError(null);
    setBannerSuccess(null);

    try {
      const url = getClubBannerPublicUrl(club.banner_image_url, false);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load banner image');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setSelectedBannerFile(null); // Clear file selection
      setBannerCropImageUrl(objectUrl);
      setBannerCropModalOpen(true);
    } catch (err) {
      console.error('Error loading banner for crop:', err);
      setBannerError(err.message || 'Failed to load banner for cropping');
    }
  };

  const handleBannerCropConfirm = async (croppedBlob) => {
    if (!clubId) return;

    setBannerError(null);
    setBannerSuccess(null);
    setUploadingBanner(true);

    try {
      const file = new File([croppedBlob], 'banner.jpg', { type: 'image/jpeg' });
      const result = await uploadClubBanner(clubId, file, {
        resize: true,
        maxDimension: 1200,
        quality: 0.85
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload banner');
      }

      setBannerSuccess('Banner uploaded successfully!');
      setSelectedBannerFile(null);
      if (bannerCropImageUrl) {
        URL.revokeObjectURL(bannerCropImageUrl);
      }
      setBannerCropImageUrl(null);
      setBannerCropModalOpen(false);
      await fetchClubDetails();
      setTimeout(() => setBannerSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading banner:', err);
      setBannerError(err.message || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleBannerCropClose = () => {
    if (bannerCropImageUrl) {
      URL.revokeObjectURL(bannerCropImageUrl);
    }
    setBannerCropImageUrl(null);
    setSelectedBannerFile(null);
    setBannerCropModalOpen(false);
  };

  const handleBannerRemove = async () => {
    if (!club?.banner_image_url) return;

    setBannerError(null);
    setBannerSuccess(null);
    setRemovingBanner(true);

    try {
      const result = await removeClubBanner(clubId, club.banner_image_url);

      if (!result.success) {
        throw new Error(result.error || 'Failed to remove banner');
      }

      setBannerSuccess('Banner removed successfully!');
      
      // Refresh club data
      await fetchClubDetails();

      // Clear success message after 3 seconds
      setTimeout(() => setBannerSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing banner:', err);
      setBannerError(err.message || 'Failed to remove banner');
    } finally {
      setRemovingBanner(false);
    }
  };

  const handleLogoFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLogoError(null);
    setLogoSuccess(null);

    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(`File is too large. Maximum size is ${LOGO_MAX_BYTES / (1024 * 1024)}MB.`);
      event.target.value = '';
      return;
    }

    const type = (file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type)) {
      setLogoError('Invalid file type. Use JPG, PNG or WebP.');
      event.target.value = '';
      return;
    }

    setSelectedLogoFile(file);
    setLogoCropImageUrl(null);
    setLogoCropModalOpen(true);
    event.target.value = '';
  };

  const handleLogoCropClick = async () => {
    if (!club?.logo_image_url) return;

    setLogoError(null);
    setLogoSuccess(null);

    try {
      const url = getClubLogoPublicUrl(club.logo_image_url, false);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load logo image');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setSelectedLogoFile(null);
      setLogoCropImageUrl(objectUrl);
      setLogoCropModalOpen(true);
    } catch (err) {
      console.error('Error loading logo for crop:', err);
      setLogoError(err.message || 'Failed to load logo for cropping');
    }
  };

  const handleLogoCropConfirm = async (croppedBlob) => {
    if (!clubId) return;

    setLogoError(null);
    setLogoSuccess(null);
    setUploadingLogo(true);

    try {
      const file = new File([croppedBlob], 'logo.jpg', { type: 'image/jpeg' });
      const result = await uploadClubLogo(clubId, file, {
        resize: true,
        maxDimension: 400,
        quality: 0.9
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload logo');
      }

      setLogoSuccess('Logo uploaded successfully!');
      setSelectedLogoFile(null);
      if (logoCropImageUrl) {
        URL.revokeObjectURL(logoCropImageUrl);
      }
      setLogoCropImageUrl(null);
      setLogoCropModalOpen(false);
      await fetchClubDetails();
      setTimeout(() => setLogoSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setLogoError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoCropClose = () => {
    if (logoCropImageUrl) {
      URL.revokeObjectURL(logoCropImageUrl);
    }
    setLogoCropImageUrl(null);
    setSelectedLogoFile(null);
    setLogoCropModalOpen(false);
  };

  const handleLogoRemove = async () => {
    if (!club?.logo_image_url) return;

    setLogoError(null);
    setLogoSuccess(null);
    setRemovingLogo(true);

    try {
      const result = await removeClubLogo(clubId, club.logo_image_url);

      if (!result.success) {
        throw new Error(result.error || 'Failed to remove logo');
      }

      setLogoSuccess('Logo removed successfully!');
      await fetchClubDetails();
      setTimeout(() => setLogoSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing logo:', err);
      setLogoError(err.message || 'Failed to remove logo');
    } finally {
      setRemovingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-text-primary">Loading...</Typography>
          <Typography variant="muted">Loading club settings</Typography>
        </div>
      </div>
    );
  }

  if (error && !club) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-text-primary">Error</Typography>
          <Typography variant="muted" className="mb-4">{error}</Typography>
          <Button
            onClick={() => navigate('/my-clubs')}
            variant="primary"
          >
            Back to My Clubs
          </Button>
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-text-primary">Club not found</Typography>
          <Button
            onClick={() => navigate('/my-clubs')}
            variant="primary"
          >
            Back to My Clubs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <BackButton
            onClick={() => {
              if (fromMyClubs || fromManageClubs) {
                navigate(`/club-management/${clubId}`, {
                  state: {
                    fromMyClubs: fromMyClubs,
                    fromManageClubs: fromManageClubs
                  }
                });
              } else if (fromHomepage) {
                navigate('/');
              } else {
                navigate(`/club-management/${clubId}`);
              }
            }}
          >
            Back to Management
          </BackButton>
        </div>

        <div className="text-center mb-8">
          <Heading level={1} className="text-display text-text-primary mb-4">
            Club Settings
          </Heading>
          <Text className="text-body-large text-text-secondary max-w-2xl mx-auto">
            {isEditing ? 'Edit club information' : 'View and manage basic club information'}
          </Text>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <Card variant="success" className="mb-6">
            <Typography variant="body" className="text-success-dark">{success}</Typography>
          </Card>
        )}
        {error && (
          <Card variant="danger" className="mb-6">
            <Typography variant="body" className="text-danger-dark">{error}</Typography>
          </Card>
        )}

        {/* Club Information Card */}
        <Card className="mb-8">
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div>
              <Heading level={3} className="text-text-primary mb-4">Basic Information</Heading>
              <div className="space-y-4">
                <FormField
                  label="Club Name *"
                  error={formErrors.name}
                >
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter club name"
                    disabled={!isEditing}
                    required
                  />
                </FormField>

                <FormField
                  label="Location"
                  error={formErrors.location}
                >
                  <Input
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., London, UK"
                    disabled={!isEditing}
                  />
                </FormField>

                <FormField
                  label="Location Details"
                  error={formErrors.location_details}
                >
                  <Textarea
                    value={formData.location_details}
                    onChange={(e) => handleInputChange('location_details', e.target.value)}
                    placeholder="Additional location details (e.g., specific address, directions)"
                    disabled={!isEditing}
                    rows={3}
                  />
                </FormField>

                <FormField
                  label="Description"
                  error={formErrors.description}
                >
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Club description"
                    disabled={!isEditing}
                    rows={4}
                  />
                </FormField>

                <FormField
                  label="Strength Rating"
                  error={formErrors.strength_rating}
                  helpText="Club strength rating (typically 0-3000)"
                >
                  <Input
                    type="number"
                    value={formData.strength_rating}
                    onChange={(e) => handleInputChange('strength_rating', parseInt(e.target.value) || 0)}
                    min={0}
                    disabled={!isEditing}
                  />
                </FormField>

                <FormField
                  label="Visibility"
                  error={formErrors.is_public}
                >
                  <Checkbox
                    checked={formData.is_public}
                    onChange={(e) => handleInputChange('is_public', e.target.checked)}
                    disabled={!isEditing}
                    label="Make this club public (visible to all users)"
                  />
                </FormField>
              </div>
            </div>

            {/* Club Banner Section */}
            <div className="border-t border-border-medium pt-6">
              <Heading level={3} className="text-text-primary mb-4">Club Banner</Heading>
              <div className="space-y-4">
                {/* Current Banner Preview */}
                <div>
                  <Text className="text-sm text-text-secondary mb-2">Current Banner</Text>
                  <div className="max-w-md">
                    <ClubBanner
                      bannerImageUrl={club?.banner_image_url}
                      clubName={club?.name}
                      location={club?.location}
                      variant="card"
                    />
                  </div>
                </div>

                {/* Banner Actions */}
                <div className="flex gap-3 items-center flex-wrap">
                  <div>
                    <input
                      type="file"
                      id="banner-upload"
                      accept={BANNER_ACCEPT}
                      onChange={handleBannerFileSelect}
                      disabled={uploadingBanner || !isEditing}
                      className="hidden"
                    />
                    <label htmlFor="banner-upload">
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={uploadingBanner || !isEditing}
                        onClick={() => document.getElementById('banner-upload')?.click()}
                      >
                        {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                      </Button>
                    </label>
                  </div>

                  {club?.banner_image_url && (
                    <>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={handleBannerCropClick}
                        disabled={uploadingBanner || !isEditing}
                      >
                        Crop image
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={handleBannerRemove}
                        disabled={removingBanner || !isEditing}
                      >
                        {removingBanner ? 'Removing...' : 'Remove Banner'}
                      </Button>
                    </>
                  )}
                </div>

                <Text className="text-xs text-text-muted">
                  Recommended: 1200x400px or wider. Max size: 2MB. Formats: JPG, PNG, WebP
                </Text>

                {/* Banner Messages */}
                {bannerSuccess && (
                  <div className="bg-success-light border border-success rounded-md p-3">
                    <Text className="text-sm text-success-dark">{bannerSuccess}</Text>
                  </div>
                )}
                {bannerError && (
                  <div className="bg-danger-light border border-danger rounded-md p-3">
                    <Text className="text-sm text-danger-dark">{bannerError}</Text>
                  </div>
                )}
              </div>
            </div>

            <ImageCropModal
              isOpen={bannerCropModalOpen}
              onClose={handleBannerCropClose}
              image={selectedBannerFile || bannerCropImageUrl}
              onConfirm={handleBannerCropConfirm}
              aspect={BANNER_ASPECT}
              title="Crop club banner"
              confirmLabel="Use this image"
              outputOptions={{ format: 'image/jpeg', quality: 0.9, maxWidth: 1200 }}
            />

            {/* Club Logo Section */}
            <div className="border-t border-border-medium pt-6">
              <Heading level={3} className="text-text-primary mb-1">Club Logo</Heading>
              <Text className="text-sm text-text-secondary mb-4">
                A square logo used in compact spaces across the app. Displayed as a circle.
              </Text>
              <div className="space-y-4">
                {/* Current Logo Preview */}
                <div>
                  <Text className="text-sm text-text-secondary mb-2">Current Logo</Text>
                  <ClubLogo
                    logoImageUrl={club?.logo_image_url}
                    clubName={club?.name}
                    size="lg"
                  />
                </div>

                {/* Logo Actions */}
                <div className="flex gap-3 items-center flex-wrap">
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept={LOGO_ACCEPT}
                      onChange={handleLogoFileSelect}
                      disabled={uploadingLogo || !isEditing}
                      className="hidden"
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={uploadingLogo || !isEditing}
                        onClick={() => document.getElementById('logo-upload')?.click()}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                  </div>

                  {club?.logo_image_url && (
                    <>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={handleLogoCropClick}
                        disabled={uploadingLogo || !isEditing}
                      >
                        Crop image
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={handleLogoRemove}
                        disabled={removingLogo || !isEditing}
                      >
                        {removingLogo ? 'Removing...' : 'Remove Logo'}
                      </Button>
                    </>
                  )}
                </div>

                <Text className="text-xs text-text-muted">
                  Square image recommended. Max size: 5MB. Formats: JPG, PNG, WebP
                </Text>

                {logoSuccess && (
                  <div className="bg-success-light border border-success rounded-md p-3">
                    <Text className="text-sm text-success-dark">{logoSuccess}</Text>
                  </div>
                )}
                {logoError && (
                  <div className="bg-danger-light border border-danger rounded-md p-3">
                    <Text className="text-sm text-danger-dark">{logoError}</Text>
                  </div>
                )}
              </div>
            </div>

            <ImageCropModal
              isOpen={logoCropModalOpen}
              onClose={handleLogoCropClose}
              image={selectedLogoFile || logoCropImageUrl}
              onConfirm={handleLogoCropConfirm}
              aspect={LOGO_ASPECT}
              cropShape="round"
              title="Crop club logo"
              confirmLabel="Use this image"
              outputOptions={{ format: 'image/jpeg', quality: 0.9, maxWidth: 400 }}
            />

            {/* Contact Information Section */}
            <div className="border-t border-border-medium pt-6">
              <Heading level={3} className="text-text-primary mb-4">Contact Information</Heading>
              <div className="space-y-4">
                <FormField
                  label="Contact Email"
                  error={formErrors.contact_email}
                  helpText="Email address for club inquiries"
                >
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="contact@club.com"
                    disabled={!isEditing}
                  />
                </FormField>

                <FormField
                  label="Website URL"
                  error={formErrors.website_url}
                  helpText="Full URL including https://"
                >
                  <Input
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => handleInputChange('website_url', e.target.value)}
                    placeholder="https://www.clubwebsite.com"
                    disabled={!isEditing}
                  />
                </FormField>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="border-t border-border-medium pt-6">
              <Heading level={3} className="text-text-primary mb-4">Social Media</Heading>
              <div className="space-y-4">
                <FormField
                  label="Facebook URL"
                  error={formErrors.facebook}
                >
                  <Input
                    type="url"
                    value={formData.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    placeholder="https://www.facebook.com/yourclub"
                    disabled={!isEditing}
                  />
                </FormField>

                <FormField
                  label="Twitter URL"
                  error={formErrors.twitter}
                >
                  <Input
                    type="url"
                    value={formData.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    placeholder="https://twitter.com/yourclub"
                    disabled={!isEditing}
                  />
                </FormField>

                <FormField
                  label="Instagram URL"
                  error={formErrors.instagram}
                >
                  <Input
                    type="url"
                    value={formData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    placeholder="https://www.instagram.com/yourclub"
                    disabled={!isEditing}
                  />
                </FormField>
              </div>
            </div>

            {/* Session Configuration — hidden (legacy; pending redesign) */}

            {/* Read-Only Information Section */}
            <div className="border-t border-border-medium pt-6">
              <Heading level={3} className="text-text-primary mb-4">Club Information</Heading>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <Typography variant="body" className="text-text-secondary">Member Count</Typography>
                  <Typography variant="body" className="text-text-primary font-medium">
                    {club.member_count || 0} members
                  </Typography>
                </div>
                <div className="flex justify-between">
                  <Typography variant="body" className="text-text-secondary">Created</Typography>
                  <Typography variant="body" className="text-text-primary font-medium">
                    {formatDate(club.created_at)}
                  </Typography>
                </div>
                {club.updated_at && (
                  <div className="flex justify-between">
                    <Typography variant="body" className="text-text-secondary">Last Updated</Typography>
                    <Typography variant="body" className="text-text-primary font-medium">
                      {formatDate(club.updated_at)}
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-border-medium pt-6 flex justify-end space-x-4">
              {!isEditing ? (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="primary"
                  disabled={saving}
                >
                  Edit Settings
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClubSettings;
